import { ILightFlags } from "./types";
import { Box } from "./Box";
import { SceneImage } from "./SceneImage";
import { CloudMaterial } from "./CloudMaterial";

declare const THREE: any;

interface ISize {
  length: number;
  height: number;
}

export class LightMapGenerator {
  private static textureLoader = new THREE.TextureLoader();

  private static rotateTransform = new THREE.Matrix4().makeRotationX(
    THREE.Math.degToRad(-90)
  );

  private static rotateAndTranslateTransform = LightMapGenerator.rotateTransform
    .clone()
    .multiply(new THREE.Matrix4().makeTranslation(0, 0.5, 0));

  private static topRailPlaneTransform = LightMapGenerator.rotateTransform
    .clone()
    .multiply(new THREE.Matrix4().makeTranslation(0, 0.4, 0));

  private static lightMapBasePath = "/assets/images/railingLightMap/";

  private renderer;
  private camera;
  private scene;
  private topRailLightTexture;
  private postLightTexture;
  private bottomLightTexture;

  private stairsPostLightSize: ISize = {
    length: 60,
    height: 30
  };

  private floorPostLightSize: ISize = {
    length: 80,
    height: 45
  };

  constructor(showCanvas = false) {
    const renderer = new THREE.WebGLRenderer({
      antialias: true,
      preserveDrawingBuffer: true,
      alpha: true
    });
    renderer.setPixelRatio(window.devicePixelRatio);
    renderer.setClearColor(0x000000, 1);
    this.renderer = renderer;
    const camera = new THREE.OrthographicCamera();
    camera.near = -1000;
    camera.far = 1000;
    camera.rotateX(-Math.PI / 2);
    this.camera = camera;
    if (showCanvas) {
      const canvas = renderer.domElement as HTMLCanvasElement;
      canvas.style.position = "fixed";
      canvas.style.right = "0";
      canvas.style.bottom = "0";
      canvas.style.zIndex = "1000";
      document.body.appendChild(canvas);
    }
    this.scene = new THREE.Scene();
    const textureLoader = LightMapGenerator.textureLoader;
    this.topRailLightTexture = textureLoader.load(
      `${LightMapGenerator.lightMapBasePath}topLight2.png`
    );
    this.postLightTexture = textureLoader.load(
      `${LightMapGenerator.lightMapBasePath}postLight.png`
    );
    this.bottomLightTexture = textureLoader.load(
      `${LightMapGenerator.lightMapBasePath}bottomLight.png`
    );
  }

  private static basicMaterialFactory = (materialSettings?: object) => {
    return new THREE.MeshBasicMaterial({
      color: 0xffffff,
      ...(materialSettings || {}),
      side: THREE.DoubleSide,
      transparent: true
    });
  };

  private static createPlane(
    width: number,
    height: number,
    planeTransform,
    material
  ) {
    const geometry = new THREE.PlaneBufferGeometry(1, 1).applyMatrix(
      planeTransform
    );
    const mesh = new THREE.Mesh(geometry, material);
    mesh.scale.set(width, 1, height);
    return mesh;
  }

  private cleanScene() {
    const scene = this.scene;
    scene.children
      .map(ch => ch.id)
      .forEach(id => {
        const mesh = scene.getObjectById(id);
        scene.remove(mesh);
        mesh.geometry.dispose();
        mesh.material.dispose();
      });
  }

  private forBottomRail(length, railPosition, yRotation: number) {
    const plane = LightMapGenerator.createPlane(
      length,
      10,
      LightMapGenerator.rotateTransform,
      LightMapGenerator.basicMaterialFactory({
        map: this.bottomLightTexture,
        opacity: 0.9
      })
    );
    plane.position.copy(railPosition);
    plane.rotation.y = yRotation;
    return plane;
  }

  private forTopRail(length, railPosition, yRotation: number) {
    const topLightPlane = LightMapGenerator.createPlane(
      length,
      90,
      LightMapGenerator.topRailPlaneTransform,
      new CloudMaterial()
    );
    topLightPlane.position.copy(railPosition);
    topLightPlane.rotation.y = yRotation;
    return topLightPlane;
  }

  private forPosts(size: ISize, position, yRotation: number) {
    const postLightPlane = LightMapGenerator.createPlane(
      size.length,
      size.height,
      LightMapGenerator.rotateAndTranslateTransform,
      LightMapGenerator.basicMaterialFactory({
        map: this.postLightTexture,
        opacity: 0.5
      })
    );
    postLightPlane.position.set(position.x, 0, position.z);
    postLightPlane.rotation.y = yRotation;
    return postLightPlane;
  }

  generate(mainScene, box: Box, flags: ILightFlags): HTMLCanvasElement {
    mainScene.traverse(obj => {
      if (obj.name === "BaseRailContainer") {
        const {
          excludeFromLightMap,
          isLeft,
          nonHorizontal,
          start,
          end
        } = obj.userData;

        let len = 0;

        if (nonHorizontal) {
          len = start
            .clone()
            .setY(0)
            .distanceTo(end.clone().setY(0));
        } else {
          len = start.distanceTo(end);
        }

        if (flags.top && !excludeFromLightMap) {
          const rotation =
            !isLeft && nonHorizontal
              ? obj.rotation.y + THREE.Math.degToRad(180)
              : obj.rotation.y;

          const topRail = this.forTopRail(len - 10, obj.position, rotation);
          this.scene.add(topRail);
        }

        if (flags.bottom && !excludeFromLightMap) {
          const bottomRail = this.forBottomRail(
            len,
            obj.position,
            obj.rotation.y
          );
          this.scene.add(bottomRail);
        }
      }

      if (obj.name === "PostContainer") {
        if (flags.post) {
          const light = obj.getObjectByName("PostAccentLight");
          if (light) {
            obj.updateMatrixWorld();
            const position = obj.localToWorld(light.position.clone());
            const size = obj.userData.onStairs
              ? this.stairsPostLightSize
              : this.floorPostLightSize;
            const postLight = this.forPosts(size, position, light.rotation.y);
            this.scene.add(postLight);
          }
        }
      }
    });

    const { renderer, camera, scene } = this;
    const { length, height } = box;
    camera.position.copy(box.center);
    camera.left = -length / 2;
    camera.right = length / 2;
    camera.top = height / 2;
    camera.bottom = -height / 2;
    camera.updateMatrixWorld();
    camera.updateProjectionMatrix();
    renderer.setSize(length, height);
    renderer.render(scene, camera);

    const screenshot = SceneImage.getScreenshot(renderer);

    this.cleanScene();

    return screenshot;
  }
}



// WEBPACK FOOTER //
// ./src/app/services/lightMap/LightMapGenerator.ts